import React, { useMemo } from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { useWatch } from 'react-hook-form';

// Import our custom components
import Questions from './Questions';
import ConditionalFields from './ConditionalFields';

// Import utilities
import { isFailingResponse } from '../utils/constants';

/**
 * QuestionArea Component
 * 
 * Represents a group of related questions within a survey section.
 * For example, within the "Kitchen" section, there might be areas like
 * "Equipment", "Cleanliness", etc.
 * 
 * This component:
 * - Displays the area title with section/area numbering
 * - Renders all questions in the area
 * - Shows conditional follow-up fields when questions fail
 * - Manages the visibility of explanation, responsibility, and image fields
 * 
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object
 * @param {Object} props.errors - Form validation errors
 * @param {Array} props.questions - Array of question objects for this area
 * @param {string} props.areaKey - The area name/key
 * @param {number} props.areaIndex - Zero-based area index within the section
 * @param {number} props.sectionIndex - Zero-based section index
 * @param {string} props.sectionKey - The section name/key
 */
const QuestionArea = ({
  control,
  errors,
  questions,
  areaKey,
  areaIndex,
  sectionIndex,
  sectionKey,
}) => {
  // Calculate display numbers (1-based for user display)
  const sectionNumber = sectionIndex + 1;
  const areaNumber = areaIndex + 1;
  const fieldPrefix = `${sectionNumber}-${areaNumber}`;

  // Use useWatch to watch all form values
  const watchedValues = useWatch({ control });

  /**
   * Determines if conditional fields should be shown based on question responses
   * Uses useMemo to prevent unnecessary re-renders that cause textarea focus loss
   * Conditional fields (explanation, responsibility, images) are required when any question
   * in the area has a failing response.
   */
  const showConditionalFields = useMemo(() => {
    if (!watchedValues || typeof watchedValues !== 'object') return false;

    // Get all question fields for this area - use exact prefix matching
    // fieldPrefix is like "2-1", we want to match "2-1-37" but not "2-10-55"
    const exactPrefix = `${fieldPrefix}-`;
    const areaFields = Object.keys(watchedValues).filter(fieldName => 
      fieldName.startsWith(exactPrefix) && 
      !fieldName.includes('-explain') && 
      !fieldName.includes('-responsible') && 
      !fieldName.includes('-files')
    );

    // Check if any field has a failing response
    const hasFailingResponse = areaFields.some(fieldName => {
      return isFailingResponse(watchedValues[fieldName]);
    });


    return hasFailingResponse;
  }, [watchedValues, fieldPrefix]);

  // The useWatch hook above automatically handles initial state and updates

  return (
    <div>
      {/* Area Header */}
      <div
        style={{
          padding: 16,
          fontFamily: 'Lato',
          fontSize: 16,
        }}
      >
        {sectionNumber}.{areaNumber} - {areaKey}

        {/* Questions Component */}
        <Questions
          control={control}
          errors={errors}
          questions={questions}
          sectionIndex={sectionIndex}
          areaIndex={areaIndex}
          fieldPrefix={fieldPrefix}
        />
      </div>

      {/* Conditional Fields (shown when any question in the area fails) */}
      {showConditionalFields && (
        <Grid 
          container 
          spacing={2}
          style={{ 
            padding: 20,
            backgroundColor: '#f5f5f5',
            marginTop: 8
          }}
        >
          <Grid item xs={12}>
            <Typography 
              variant="subtitle2" 
              color="error"
              style={{ 
                fontFamily: 'Lato',
                marginBottom: 16,
                fontWeight: 'bold'
              }}
            >
              Additional Information Required
            </Typography>
            <Typography 
              variant="body2" 
              color="textSecondary"
              style={{ 
                fontFamily: 'Lato',
                marginBottom: 16
              }}
            >
              Please provide additional details about the issues found in this area.
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <ConditionalFields
              control={control}
              errors={errors}
              fieldPrefix={fieldPrefix}
              sectionNumber={sectionNumber}
              areaNumber={areaNumber}
              areaKey={areaKey}
            />
          </Grid>
        </Grid>
      )}

      {/* Divider between areas */}
      <Divider style={{ marginTop: 16 }} />
    </div>
  );
};

export default QuestionArea;